import React, { useCallback, useImperativeHandle, useRef } from 'react';
import clsx from 'clsx';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { type EditorState } from 'lexical';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { PlainTextPlugin } from '@lexical/react/LexicalPlainTextPlugin';

import { convertLexicalStateToFormulaString } from '../../../../utils/formulas/formulas-lexical';
import { FormulaReferenceOption } from '../../../../utils/formulas/types';
import { ActionsBridgePlugin, FormulaEditorLexicalHandle } from './actions-bridge-plugin';
import { EditablePlugin } from './editable-plugin';
import { InitialContentPlugin } from './initial-content-plugin';
import { MentionNode } from './mention-node';
import { MentionsTypeaheadPlugin } from './mentions-typeahead-plugin';
import styles from './formula-editor.scss';

export interface FormulaEditorLexicalProps {
  ariaLabel?: string;
  /**
   * When true, focuses the editor on mount and places the cursor at the end of any
   * existing formula content (or leaves it ready for typing when there is none).
   */
  autoFocus?: boolean;
  className?: string;
  editorInputClassName?: string;
  initialContentOptions?: FormulaReferenceOption[];
  initialEditorOptions?: FormulaReferenceOption[];
  initialFormulaContent?: string;
  newStyling?: boolean;
  nonSelectableOptionIds?: string[];
  onFormulaChange?: (formula: string) => void;
  readOnly?: boolean;
}

const editorConfig = {
  namespace: 'formula-editor',
  nodes: [MentionNode],
  onError(error: Error) {
    throw error;
  }
};

/**
 * Lexical-based replacement for the draft-js `FormulaEditor`. Renders the same "@name" mention
 * chips for references, backed by the translation layer in `utils/formulas/formulas-lexical.ts`
 * so that existing formulas saved from the draft-js editor render identically here.
 */
export const FormulaEditorLexical = React.forwardRef<FormulaEditorLexicalHandle, FormulaEditorLexicalProps>(
  (
    {
      ariaLabel,
      autoFocus = false,
      className,
      editorInputClassName,
      initialContentOptions,
      initialEditorOptions = [],
      initialFormulaContent = '',
      newStyling,
      nonSelectableOptionIds,
      onFormulaChange,
      readOnly = false
    },
    forwardedRef
  ) => {
    const actionsRef = useRef<FormulaEditorLexicalHandle | null>(null);
    const lastFormulaStringRef = useRef<string>(initialFormulaContent);

    useImperativeHandle(forwardedRef, () => ({
      insertTextAtCursor: (text: string) => actionsRef.current?.insertTextAtCursor(text)
    }));

    const handleChange = useCallback(
      (editorState: EditorState) => {
        const newFormulaString = convertLexicalStateToFormulaString(editorState);
        if (newFormulaString !== lastFormulaStringRef.current) {
          lastFormulaStringRef.current = newFormulaString;
          onFormulaChange?.(newFormulaString);
        }
      },
      [onFormulaChange]
    );

    return (
      <LexicalComposer initialConfig={editorConfig}>
        <div
          className={clsx(styles.editor, className, { [styles.newStyling]: newStyling })}
          data-testid="formula-editor-lexical"
        >
          <PlainTextPlugin
            contentEditable={
              <ContentEditable
                className={clsx(styles.editorInput, editorInputClassName)}
                aria-label={ariaLabel ?? 'Enter formula'}
              />
            }
            placeholder={null}
            ErrorBoundary={LexicalErrorBoundary}
          />
          <HistoryPlugin />
          <OnChangePlugin onChange={handleChange} />
          <InitialContentPlugin
            autoFocus={autoFocus}
            formulaContent={initialFormulaContent}
            initialEditorOptions={initialContentOptions ?? initialEditorOptions}
          />
          <EditablePlugin readOnly={readOnly} />
          {!readOnly && <ActionsBridgePlugin actionsRef={actionsRef} />}
          {!readOnly && (
            <MentionsTypeaheadPlugin
              nonSelectableOptionIds={nonSelectableOptionIds}
              options={initialEditorOptions}
            />
          )}
        </div>
      </LexicalComposer>
    );
  }
);

FormulaEditorLexical.displayName = 'FormulaEditorLexical';

export type { FormulaEditorLexicalHandle } from './actions-bridge-plugin';
